import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Layers, Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { getActiveMetaPages } from '@/services/api';
import { getUtilityTemplateGroupsAll } from '@/services/api/broadcast';
import {
    createEsteira,
    getEsteira,
    updateEsteira,
    type CreateUtilityEsteiraRequest,
} from '@/services/api/utility-esteiras';
import type { Page, UtilityTemplateResolutionEntry } from '@/types/broadcast';
import MultiTemplatePicker from './components/MultiTemplatePicker';
import type { MultiTemplateOverlapResult, MultiTemplateSelectionEntry } from './types';
import { buildUtilityTemplateResolution } from './utils/multiTemplateResolution';

/**
 * Builder de Esteira — tela dedicada (fora do wizard de broadcast) para montar
 * um pack reutilizável de páginas + templates com overlap resolvido + auxiliar
 * de texto opcional. Ver PRD-esteira-multi-template-utility.md, seção 3 e 6.
 */
export default function EsteiraBuilder() {
    const { id } = useParams<{ id: string }>();
    const isEditing = !!id;
    const esteiraId = id ? Number(id) : undefined;
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    const [name, setName] = useState('');
    const [selectedPages, setSelectedPages] = useState<Page[]>([]);
    const [selection, setSelection] = useState<MultiTemplateSelectionEntry[]>([]);
    const [conflictResolutions, setConflictResolutions] = useState<Record<string, number>>({});
    const [overlapResult, setOverlapResult] = useState<MultiTemplateOverlapResult | null>(null);
    const [ready, setReady] = useState(true);
    const [hydrated, setHydrated] = useState(!isEditing);

    const { data: pages = [] as Page[], isLoading: loadingPages } = useQuery({
        queryKey: ['active-pages'],
        queryFn: getActiveMetaPages,
        staleTime: 1000 * 60 * 5,
    });

    const { data: allUtilityGroups = [], isLoading: loadingGroups } = useQuery({
        queryKey: ['utility-groups'],
        queryFn: getUtilityTemplateGroupsAll,
        staleTime: 1000 * 60 * 5,
    });

    const utilityGroups = useMemo(
        () => allUtilityGroups.filter(g => g.is_mine === true && (g.approved_count ?? 0) > 0),
        [allUtilityGroups]
    );

    const { data: existingEsteira, isLoading: loadingEsteira } = useQuery({
        queryKey: ['utility-esteira', esteiraId],
        queryFn: () => getEsteira(esteiraId as number),
        enabled: isEditing,
    });

    // Popula o estado do builder a partir da Esteira existente assim que
    // temos tanto o registro quanto o catálogo de páginas/templates disponível
    // (precisamos dos objetos completos de Page/UtilityTemplateGroup, não só dos ids salvos).
    useEffect(() => {
        if (!isEditing || hydrated) return;
        if (!existingEsteira || loadingPages || loadingGroups) return;

        setName(existingEsteira.name);

        const pageById = new Map(pages.map(p => [p.page_id, p]));
        setSelectedPages(
            existingEsteira.page_ids.map(pid => pageById.get(pid)).filter((p): p is Page => !!p)
        );

        const groupById = new Map(utilityGroups.map(g => [g.id, g]));
        const nextSelection: MultiTemplateSelectionEntry[] = [];
        existingEsteira.template_resolution.forEach((entry, idx) => {
            const group = groupById.get(entry.group_id);
            if (!group) return;
            nextSelection.push({
                groupId: group.id,
                priority: idx,
                templateName: group.template_name,
                title: group.title,
                imageUrl: group.image_url ?? null,
                thumbnailUrl: group.thumbnail_url ?? null,
                approvedCount: group.approved_count,
                secondaryGroupId: entry.secondary_group_id ?? null,
            });
        });
        setSelection(nextSelection);

        setHydrated(true);
    }, [isEditing, hydrated, existingEsteira, loadingPages, loadingGroups, pages, utilityGroups]);

    const hasPagesAndTemplates = selectedPages.length > 0 && selection.length > 0;

    const buildResolution = (): UtilityTemplateResolutionEntry[] | null => {
        if (selection.length === 0) return [];
        if (selection.length === 1) {
            const only = selection[0];
            return [
                {
                    group_id: only.groupId,
                    page_ids: selectedPages.map(p => p.page_id),
                    ...(only.secondaryGroupId != null ? { secondary_group_id: only.secondaryGroupId } : {}),
                },
            ];
        }
        if (!overlapResult) return null;
        const built = buildUtilityTemplateResolution({
            conflictGroups: overlapResult.conflictGroups,
            exclusiveByGroup: overlapResult.exclusiveByGroup,
            uncoveredPageIds: overlapResult.uncoveredPageIds,
            conflictResolutions,
            selection,
        });
        if (built.hasPendingConflicts) return null;
        return built.resolution;
    };

    const saveMutation = useMutation({
        mutationFn: async () => {
            const resolution = buildResolution();
            if (resolution === null) {
                throw new Error('Resolva todos os conflitos de sobreposição antes de salvar.');
            }
            const payload: CreateUtilityEsteiraRequest = {
                name: name.trim(),
                page_ids: selectedPages.map(p => p.page_id),
                template_resolution: resolution,
            };
            if (isEditing && esteiraId) {
                return updateEsteira(esteiraId, payload);
            }
            return createEsteira(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['utility-esteiras'] });
            toast({ title: isEditing ? 'Esteira atualizada' : 'Esteira criada' });
            navigate('/meta/utility-esteiras');
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : 'Erro ao salvar a Esteira.';
            toast({ variant: 'destructive', title: 'Erro', description: message });
        },
    });

    const canSave = !!name.trim() && hasPagesAndTemplates && ready && !saveMutation.isPending;

    if (isEditing && (loadingEsteira || !hydrated)) {
        return (
            <div className="flex flex-col items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-[#7dd3fc] mb-4" />
                <p className="text-gray-600">Carregando Esteira...</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto p-6 space-y-6">
            <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate('/meta/utility-esteiras')}>
                    <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-[#0f172a]" />
                    <h1 className="text-xl font-semibold text-gray-900">
                        {isEditing ? 'Editar Esteira' : 'Nova Esteira'}
                    </h1>
                </div>
            </div>
            <p className="text-sm text-gray-500 -mt-4">
                Monte um pack reutilizável de páginas + templates de utility com sobreposição resolvida,
                para selecionar depois na criação de broadcasts sem remontar do zero.
            </p>

            <div className="space-y-2">
                <Label htmlFor="esteira-name" className="text-sm font-medium">Nome da Esteira</Label>
                <Input
                    id="esteira-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ex: Esteira Black Friday — 400 páginas"
                    className="max-w-md"
                />
            </div>

            <MultiTemplatePicker
                pages={pages}
                utilityGroups={utilityGroups}
                loadingGroups={loadingGroups}
                selectedPages={selectedPages}
                onSelectedPagesChange={setSelectedPages}
                selection={selection}
                onSelectionChange={setSelection}
                conflictResolutions={conflictResolutions}
                onConflictResolutionsChange={setConflictResolutions}
                onReadyChange={setReady}
                onOverlapResultChange={setOverlapResult}
            />

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
                <Button variant="outline" onClick={() => navigate('/meta/utility-esteiras')}>
                    Cancelar
                </Button>
                <Button
                    onClick={() => saveMutation.mutate()}
                    disabled={!canSave}
                    className="gap-2"
                >
                    {saveMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Save className="w-4 h-4" />
                    )}
                    Salvar Esteira
                </Button>
            </div>
        </div>
    );
}
